import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Radio, Camera, GaugeCircle, HardHat, Cog } from "lucide-react";

const safetyPillars = [
  { icon: HardHat, title: "CDL-Certified Drivers", desc: "Every driver is fully licensed, background-checked, and trained on DOT best practices before turning a wheel." },
  { icon: GaugeCircle, title: "Pre-Trip Inspections", desc: "Brakes, tires, lights, load securement, and cargo restraints are inspected before every dispatch — no exceptions." },
  { icon: Radio, title: "Live Dispatch Comms", desc: "24/7 dispatch keeps constant contact with drivers, monitors ETAs, and reroutes proactively around weather or traffic." },
  { icon: Camera, title: "GPS + Load Tracking", desc: "Real-time location and status updates for every shipment — you and our team see the same picture, live." },
  { icon: Cog, title: "Routine Maintenance", desc: "Scheduled PM services, DOT-mandated inspections, and same-week repair turnaround keep our trucks road-ready." },
  { icon: ShieldCheck, title: "Full Cargo Insurance", desc: "General liability + cargo insurance on every commercial shipment. Your freight is protected door-to-door." },
];

export function SafetyPillars() {
  return (
    <section className="py-20 lg:py-28 border-b border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Safety First</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-slate-900 max-w-2xl">
              Six pillars keep every commercial delivery safe.
            </h2>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <ShieldCheck className="h-5 w-5 text-primary-brand" />
            <span>DOT-compliant · Fully insured · Zero-incident culture</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {safetyPillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.04, duration: 0.5 }}
              data-testid={`safety-pillar-${i}`}
              className="group bg-white p-8 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <p.icon className="h-8 w-8 text-primary-brand" strokeWidth={1.6} />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">#{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-heading text-lg font-bold mt-6 text-slate-900">{p.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.desc}</p>
              <div className="mt-5 h-px w-8 bg-primary-brand transition-all group-hover:w-16" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
